/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';

import Numeric from '../numeric/numeric';

// TODO Přidat vyvolávání událostí o dění v pool?
// - eventy: lent, returned, deleted, filled, drained.
// - Umožnilo by čekat na uvolnění objekty.
// TODO Rušení zdrojů. Přidat resetování objektů do výchozího stavu?
// - Doporučeno dle wiki.
// - V aktuální implementaci se zdá výhodnější resetovat objekt uživatelem, který ví co změnil.
// - Resetovat libovolný(!) element do výchozího stavu by bylo velice těžké implementovat.
// - Možné řešení problému se `srcObject`.
// TODO Zrušit implmentaci maxima?
// - Mohlo by být nahrazeno sledováním událostí a řízeno z vyšší úrovně.
/**
 * Bazén recyklovaných objektů.
 * Implementuje návrhový vzor [Object pool](https://en.wikipedia.org/wiki/Object_pool_pattern).
 * Provádí recyklaci vrácených objektů pro další použití.
 * Počet objektů lze omezit maximem (nedovolí vytváření dalších).
 *
 * Implementace je orientována na maximální výkon, jednoduchost a robustnost.
 *
 * Lze využít pro redukci garbage collection, omezení náročné inicializace nebo destrukce,
 * splnění omezení na přístup ke zdrojům apod.
 *
 * Inspirováno existujícími knihovnami:
 * [generic-pool](https://github.com/coopernurse/node-pool),
 * [deePool](https://github.com/getify/deePool).
 * @implements {Destroyable}
 */
class Pool {
  /**
   * Vytvoří instanci.
   * @param {PoolObjectFactory} factory Továrna pro vytváření nových objektů umísťených v bazénu.
   * @param {number} max Maximální počet objektů, které má bazén vytvořit.
   */
  constructor(factory, max) {
    // Kontrola povinných parametrů.
    if (!Pool.isFactory(factory)) {
      throw new TypeError(`Invalid factory ${factory}.`);
    }
    // Kontrola nepovinných parametrů.
    if (!Pool.isMax(max)) {
      throw new TypeError(`Invalid maximum ${max}.`);
    }
    /**
     * Seznam volných objektů.
     * Pro lepší výkon se při operacích provádí mutace jednoho pole.
     * @private
     * @type {Array.<PoolObject>}
     */
    this.availableArray = [];
    /**
     * Továrna pro vytváření nových objektů.
     * @private
     * @type {PoolObjectFactory}
     */
    this.factory = factory;
    /**
     * Seznam zapůjčených objektů.
     * Pro lepší výkon se při operacích provádí mutace jednoho pole.
     * @private
     * @type {Array.<PoolObject>}
     */
    this.lentArray = [];
    /**
     * Maximální počet objektů v bazénu.
     * @private
     * @type {number}
     */
    this.max = max;
  }

  /**
   * Obdrží zpět poškozený zapůjčený objekt.
   * Takový objekt není dále recyklovaný a nahradí se při další zápůjčce za nový.
   * Volající by měl používat tuto metodu, pokud již objekt nelze obnovit do stavu pro další použití.
   * @param {PoolObject} object Vrácený objekt.
   * @returns {undefined} Nic.
   */
  delete(object) {
    // Objekt se odebere ze zapůjčených.
    this.removeLent(object);
    // Objekt se smaže.
    this.factory.delete(object);
  }

  /** @inheritdoc */
  destroy() {
    const lent = this.getLent();
    if (lent !== 0) {
      throw new Error(`There are some lent objects (${lent}).`);
    }
    const available = this.getAvailable();
    this.drain(available);
  }

  /**
   * Vypustí bazén objektů tím, že smaže nepoužívané objekty.
   * @param {number} count Počet objektů.
   * @returns {undefined} Nic.
   */
  drain(count) {
    if (!Numeric.isIndex(count)) {
      throw new TypeError(`Invalid count ${count}.`);
    }
    if (count > this.getAvailable()) {
      throw new Error('Not enough objects available.');
    }
    Array(count)
      .fill()
      .forEach(() => {
        const object = this.availableArray.pop();
        this.factory.delete(object);
      });
  }

  /**
   * Naplní bazén objektů, které jsou pak ihned připravené k zapůjčení.
   * @param {number} count Počet objektů.
   * @returns {undefined} Nic.
   */
  fill(count) {
    if (!Numeric.isIndex(count)) {
      throw new TypeError(`Invalid count ${count}.`);
    }
    if (count > this.getMax() - this.getLength()) {
      throw new Error('Maximum count of objects exceeded.');
    }
    Array(count)
      .fill()
      .forEach(() => {
        const object = this.factory.create();
        this.availableArray.push(object);
      });
  }

  /**
   * Zjistí počet volných objektů, připravených na zapůjčení.
   * @returns {number} Počet objektů.
   */
  getAvailable() {
    return this.availableArray.length;
  }

  /**
   * Zjistí aktuální počet vytvořených objektů v bazénu.
   * @returns {number} Počet objektů.
   */
  getLength() {
    return this.getAvailable() + this.getLent();
  }

  /**
   * Zjistí počet zapůjčených objektů.
   * @returns {number} Počet objektů.
   */
  getLent() {
    return this.lentArray.length;
  }

  /**
   * Zjistí maximální počet vytvořených objektů v bazénu.
   * @returns {number} Počet objektů, nebo `Infinity` pokud není omezeno.
   */
  getMax() {
    return this.max;
  }

  /**
   * Ověří, jestli vstup splňuje požadavky na továrnu objektů.
   * @param {*} value Vstup k ověření.
   * @private
   * @returns {boolean} Příznak, jestli jsou požadavky splněny.
   */
  static isFactory(value) {
    return isObject(value) && isFunction(value.create);
  }

  /**
   * Ověří, jestli objekt patří mezi zapůjčené.
   * @param {PoolObject} object
   * @returns {boolean}
   */
  isLent(object) {
    return this.lentArray.includes(object);
  }

  /**
   * Ověří, jestli vstup splňuje požadavky na definici maxima objektů.
   * @param {*} value Vstup k ověření.
   * @private
   * @returns {boolean} Příznak, jestli jsou požadavky splněny.
   */
  static isMax(value) {
    return value === Infinity || Numeric.isIndex(value);
  }

  /**
   * Zapůjčí volný objekt z bazénu.
   * @returns {PoolObject} Přidělený objekt, pokud je nějaký volný.
   */
  lend() {
    // Pokud je nějaký objekt volný, tak zabrat a vrátit.
    const { availableArray, lentArray } = this;
    if (this.getAvailable() > 0) {
      // Z logiky věci by mělo být jedno, jaký objekt se z pool vybere.
      // Všechny objekty by měli být stejné.
      // Proto se z pole vybírá poslední, který lze vyjmout nejrychleji.
      const object = availableArray.pop();
      lentArray.push(object);
      return object;
    }
    // Pokud je místo pro nový objekt, tak vytvořit a vrátit.
    if (this.getMax() > this.getLength()) {
      const object = this.factory.create();
      lentArray.push(object);
      return object;
    }
    // Jinak vyhodit chybu.
    throw new Error('Maximum count of objects exceeded.');
  }

  /**
   * Obdrží zpět zapůjčený objekt.
   * Takový objekt je dále recyklovaný pro nové zapůjčení.
   * Po volání této metody je zakázáno k vrácenému objektu přistupovat, nebo ho dokonce měnit.
   * Volající je také zodpovědný za to, že objekt je v takovém stavu, v jakém byl při zapůjčení.
   * @param {PoolObject} object Vrácený objekt.
   * @returns {undefined} Nic.
   */
  receive(object) {
    // Objekt se odebere ze zapůjčených.
    this.removeLent(object);
    // Objekt se přidá do volných.
    this.availableArray.push(object);
  }

  /**
   * Odebere objekt ze zapůjčených.
   * @param {PoolObject} object Objekt k odebrání.
   * @private
   * @returns {undefined} Nic.
   */
  removeLent(object) {
    // Vracený objekt se vyhledá mezi zapůjčenými.
    const { lentArray } = this;
    const lentIndex = lentArray.indexOf(object);
    if (lentIndex === -1) {
      throw new Error(`Object ${object} is not lent.`);
    }
    // Odebere se ze zapůjčených.
    lentArray.splice(lentIndex, 1);
  }
}

export default Pool;
